import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, HardHat, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Field = ({ label, children, className = '' }) => (
  <label className={`block text-xs font-semibold text-slate-600 ${className}`}>{label}{children}</label>
);
const inp = 'mt-1 w-full h-9 rounded-lg border border-slate-300 px-2.5 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

/** Type-ahead over the field crew (free text allowed — the sheet works with names too). */
const NamePicker = ({ people, value, onChange, placeholder, testid, single = false }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return people.filter(p => !value.includes(p.name) && (!s || p.name.toLowerCase().includes(s))).slice(0, 8);
  }, [people, q, value]);
  const add = (name) => { if (!name.trim()) return; onChange(single ? [name.trim()] : [...value, name.trim()]); setQ(''); setOpen(false); };
  return (
    <div className="mt-1 rounded-lg border border-slate-300 px-2 py-1.5 flex flex-wrap gap-1 items-center focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100" data-testid={testid}>
      {value.map(n => (
        <span key={n} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-300 rounded-full pl-2 pr-1 py-0.5 text-slate-800">
          {n}<button type="button" onClick={() => onChange(value.filter(v => v !== n))} className="p-0.5 rounded-full hover:bg-slate-300" data-testid={`${testid}-remove-${n}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
      {(!single || value.length === 0) && (
        <div className="relative flex-1 min-w-[120px]">
          <input ref={ref} value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(list[0]?.name || q); } if (e.key === 'Backspace' && !q && value.length) onChange(value.slice(0, -1)); }}
            placeholder={placeholder} className="w-full h-6 text-sm outline-none bg-transparent" data-testid={`${testid}-input`} />
          {open && (list.length > 0 || q.trim()) && (
            <div className="absolute left-0 top-7 z-30 w-64 bg-white border border-slate-200 rounded-lg shadow-xl py-1 max-h-56 overflow-y-auto">
              {list.map(p => <button type="button" key={p.id} onMouseDown={() => add(p.name)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-sky-50 flex justify-between" data-testid={`${testid}-opt-${p.id}`}><span>{p.name}</span><span className="text-[10px] text-slate-400 uppercase">{(p.role || '').replace(/_/g, ' ')}</span></button>)}
              {q.trim() && !list.some(p => p.name.toLowerCase() === q.trim().toLowerCase()) && <button type="button" onMouseDown={() => add(q)} className="w-full text-left px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">Use “{q.trim()}” as typed</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EquipSelect = ({ label, options, value, onChange, testid }) => (
  <Field label={label}>
    <select value={value} onChange={e => onChange(e.target.value)} className={inp} data-testid={testid}>
      <option value="">—</option>
      {value && !options.some(o => o.id === value || o.name === value) && <option value={value}>{value}</option>}
      {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  </Field>
);

export const DailyJobEditor = ({ date, job, prefill, picklists, onClose, onSaved }) => {
  const base = job || prefill || {};
  const [f, setF] = useState({
    work_order: base.work_order || '', customer: base.customer || '', place: base.place || '', start: base.start || '07:00',
    description: base.description || '', tools: base.tools || '', contact: base.contact || '', truck: base.truck || '', trailer: base.trailer || '',
    welder: base.welder || '', forklift: base.forklift || '', foreman: base.foreman || '', crew: base.crew || [], note: base.note || '',
  });
  const [projects, setProjects] = useState([]);
  const [pq, setPq] = useState('');
  const [pOpen, setPOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF(s => ({ ...s, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/projects`, auth()).then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const matches = useMemo(() => {
    const s = pq.trim().toLowerCase();
    if (!s) return [];
    return projects.filter(p => `${p.project_number} ${p.title || ''} ${p.client_name || ''}`.toLowerCase().includes(s)).slice(0, 8);
  }, [projects, pq]);
  const pickProject = (p) => {
    setF(s => ({ ...s, work_order: p.project_number, customer: s.customer || p.client_name || '', place: s.place || p.site_address || '', description: s.description || p.title || '' }));
    setPq(''); setPOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.work_order.trim() && !f.customer.trim()) return toast.error('Pick a WO or type the customer');
    setBusy(true);
    try {
      const body = { ...f, foreman: f.foreman || '' };
      const r = job?.id
        ? await axios.put(`${API}/dailies/day/${date}/jobs/${job.id}`, body, auth())
        : await axios.post(`${API}/dailies/day/${date}/jobs`, body, auth());
      const m = r.data.mirror || {};
      toast.success(`${job ? 'Updated' : 'Added'} ${f.work_order || f.customer}${m.created || m.updated ? ` · schedule ${m.created ? `+${m.created}` : ''}${m.updated ? ` ~${m.updated}` : ''}` : ''}`);
      onSaved?.(r.data.job);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save'); }
    setBusy(false);
  };

  const people = picklists?.crew || [];
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] bg-slate-900/40 backdrop-blur-[2px] flex items-start sm:items-center justify-center p-3 overflow-y-auto" onMouseDown={onClose} data-testid="daily-job-editor">
      <form onSubmit={save} onMouseDown={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 my-4">
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-900">{job ? 'Edit job block' : prefill?.planned_crew ? 'Fill in the projection' : 'Add job block'}</h3>
            <p className="text-xs text-slate-500">{new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}{prefill?.planned_crew ? ` · projected ${prefill.planned_crew} guys` : ''}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="daily-job-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="WO#" className="relative">
            <div className="relative">
              <input value={f.work_order} onChange={e => { set('work_order')(e.target.value.replace(/\s/g, '')); setPq(e.target.value); setPOpen(true); }} onFocus={() => setPOpen(true)} onBlur={() => setTimeout(() => setPOpen(false), 150)}
                placeholder="26-5000 — type to search jobs" className={`${inp} pr-8 font-mono`} data-testid="daily-job-wo" />
              <Search className="w-4 h-4 text-slate-400 absolute right-2.5 top-3.5" />
              {pOpen && matches.length > 0 && (
                <div className="absolute left-0 top-10 z-30 w-full bg-white border border-slate-200 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto">
                  {matches.map(p => <button type="button" key={p.id || p.project_number} onMouseDown={() => pickProject(p)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-sky-50" data-testid={`daily-job-proj-${p.project_number}`}><span className="font-mono font-semibold text-sky-800">{p.project_number}</span> <span className="text-slate-700">{p.title}</span> <span className="text-slate-400 text-xs">· {p.client_name}</span></button>)}
                </div>
              )}
            </div>
          </Field>
          <Field label="Name (customer)"><input value={f.customer} onChange={e => set('customer')(e.target.value)} className={inp} data-testid="daily-job-customer" /></Field>
          <Field label="Place" className="sm:col-span-2"><input value={f.place} onChange={e => set('place')(e.target.value)} className={inp} data-testid="daily-job-place" /></Field>
          <Field label="Start"><input value={f.start} onChange={e => set('start')(e.target.value)} placeholder="07:00 or 7-3:30" className={inp} data-testid="daily-job-start" /></Field>
          <Field label="Contact"><input value={f.contact} onChange={e => set('contact')(e.target.value)} className={inp} data-testid="daily-job-contact" /></Field>
          <Field label="Descrpt" className="sm:col-span-2"><input value={f.description} onChange={e => set('description')(e.target.value)} className={inp} data-testid="daily-job-description" /></Field>
          <Field label="Tool list" className="sm:col-span-2"><input value={f.tools} onChange={e => set('tools')(e.target.value)} placeholder="Packed, outside of Tool Crib" className={inp} data-testid="daily-job-tools" /></Field>
          <EquipSelect label="Truck" options={picklists?.trucks || []} value={f.truck} onChange={set('truck')} testid="daily-job-truck" />
          <EquipSelect label="Trailer" options={picklists?.trailers || []} value={f.trailer} onChange={set('trailer')} testid="daily-job-trailer" />
          <EquipSelect label="Welder" options={picklists?.welders || []} value={f.welder} onChange={set('welder')} testid="daily-job-welder" />
          <EquipSelect label="Forklift" options={picklists?.forklifts || []} value={f.forklift} onChange={set('forklift')} testid="daily-job-forklift" />
          <Field label={<span className="inline-flex items-center gap-1"><HardHat className="w-3.5 h-3.5 text-amber-600" />MF (foreman)</span>} className="sm:col-span-2">
            <NamePicker single people={people} value={f.foreman ? [f.foreman] : []} onChange={v => set('foreman')(v[0] || '')} placeholder="Type a name…" testid="daily-job-foreman" />
          </Field>
          <Field label={`Crew (${f.crew.length})`} className="sm:col-span-2">
            <NamePicker people={people} value={f.crew} onChange={set('crew')} placeholder="Add a name, Enter to confirm…" testid="daily-job-crew" />
          </Field>
          <Field label="Note" className="sm:col-span-2"><input value={f.note} onChange={e => set('note')(e.target.value)} placeholder="e.g. Matt called in sick — short one guy" className={inp} data-testid="daily-job-note" /></Field>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-500">Saved to Ops only. The Google sheet still wins on a WO/day it covers — this block greys out if the sheet gets the same WO that day.</p>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
            <button type="submit" disabled={busy} className="h-9 px-4 rounded-lg bg-sky-700 hover:bg-sky-600 text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid="daily-job-save">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}{job ? 'Save' : 'Add to the day'}
            </button>
          </div>
        </div>
      </form>
    </div>, document.body);
};
